// 文件上传工具函数
import { API_CONFIG } from '../config/api';
import toastManager from './toastManager';

// 文件类型配置
const FILE_CONFIG = {
  IMAGE: {
    maxSize: 10 * 1024 * 1024, // 10MB
    acceptTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  },
  DOCUMENT: {
    maxSize: 10 * 1024 * 1024, // 10MB
    acceptTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    extensions: ['.pdf', '.doc', '.docx']
  },
  VIDEO: {
    maxSize: 50 * 1024 * 1024, // 50MB
    acceptTypes: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv'],
    extensions: ['.mp4', '.avi', '.mov', '.wmv']
  }
};

/**
 * 验证文件
 * @param {File} file - 文件对象
 * @param {string} type - 文件类型 ('image', 'document', 'video')
 * @returns {Object} 验证结果 {valid: boolean, message: string}
 */
export function validateFile(file, type = 'image') {
  if (!file) {
    return { valid: false, message: '请选择文件' };
  }

  const config = FILE_CONFIG[type.toUpperCase()];
  if (!config) {
    return { valid: false, message: '不支持的文件类型' };
  }

  // 检查文件大小
  if (file.size > config.maxSize) {
    const maxSizeMB = config.maxSize / (1024 * 1024);
    return { valid: false, message: `文件大小不能超过${maxSizeMB}MB` };
  }

  // 检查文件类型
  if (!config.acceptTypes.includes(file.type)) {
    return { valid: false, message: `请选择${type}格式的文件` };
  }

  return { valid: true, message: '文件验证通过' };
}

/**
 * 上传文件到服务器
 * @param {File} file - 文件对象
 * @param {string} type - 文件类型
 * @param {Object} options - 上传选项
 * @returns {Promise<Object>} 上传结果 {success: boolean, url: string, error: string}
 */
export async function uploadFile(file, type = 'image', options = {}) {
  const { showProgress = true, showSuccess = true, showError = true } = options;

  // 验证文件
  const validation = validateFile(file, type);
  if (!validation.valid) {
    if (showError) {
      toastManager.error(validation.message);
    }
    return { success: false, error: validation.message };
  }

  try {
    // 显示上传进度
    if (showProgress) {
              // 正在上传文件，不显示提示
    }

    const formData = new FormData();
    formData.append('file', file);

    // 获取认证token
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // 发送上传请求
    const response = await fetch(`${API_CONFIG.BASE_URL}/forms/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`上传失败: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // 检查是否有url字段
    if (!data || !data.url) {
      throw new Error('服务器返回的URL为空');
    }

    // 处理URL路径
    let fileUrl = data.url;
    if (fileUrl.startsWith('/uploads/')) {
      const backendHost = API_CONFIG.BASE_URL.replace(/\/api$/, '');
      fileUrl = backendHost + fileUrl;
    }

    if (showSuccess) {
      // 文件上传成功，不显示提示
    }

    return { success: true, url: fileUrl };
  } catch (error) {
    console.error('文件上传失败:', error);
    const errorMessage = `文件上传失败: ${error.message}`;
    if (showError) {
      toastManager.error(errorMessage);
    }
    return { success: false, error: errorMessage };
  }
}

/**
 * 上传图片文件
 * @param {File} file - 图片文件
 * @param {Object} options - 上传选项
 * @returns {Promise<Object>} 上传结果
 */
export async function uploadImage(file, options = {}) {
  return uploadFile(file, 'image', options);
}

/**
 * 上传文档文件
 * @param {File} file - 文档文件
 * @param {Object} options - 上传选项
 * @returns {Promise<Object>} 上传结果
 */
export async function uploadDocument(file, options = {}) {
  return uploadFile(file, 'document', options);
}

/**
 * 上传视频文件
 * @param {File} file - 视频文件
 * @param {Object} options - 上传选项
 * @returns {Promise<Object>} 上传结果
 */
export async function uploadVideo(file, options = {}) {
  return uploadFile(file, 'video', options);
}

/**
 * 创建文件选择器
 * @param {string} accept - 接受的文件类型
 * @param {Function} onSelect - 文件选择回调
 * @param {string} type - 文件类型
 */
export function createFileSelector(accept, onSelect, type = 'image') {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', accept);
  
  input.onchange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      onSelect(file);
    }
  };
  
  input.click();
}

/**
 * 获取文件大小的人类可读格式
 * @param {number} bytes - 字节数
 * @returns {string} 格式化的大小
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 获取文件扩展名
 * @param {string} filename - 文件名
 * @returns {string} 文件扩展名
 */
export function getFileExtension(filename) {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
}

/**
 * 检查是否为图片文件
 * @param {File} file - 文件对象
 * @returns {boolean} 是否为图片
 */
export function isImageFile(file) {
  return file && file.type.startsWith('image/');
}

/**
 * 检查是否为视频文件
 * @param {File} file - 文件对象
 * @returns {boolean} 是否为视频
 */
export function isVideoFile(file) {
  return file && file.type.startsWith('video/');
}

/**
 * 检查是否为文档文件
 * @param {File} file - 文件对象
 * @returns {boolean} 是否为文档
 */
export function isDocumentFile(file) {
  const documentTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];
  return file && documentTypes.includes(file.type);
}